import { useLocalSearchParams, useRouter } from 'expo-router'
import PocketBase from 'pocketbase'
import { useCallback, useState } from 'react'
import { FlatList, Platform, StyleSheet, View } from 'react-native'
import {
    ActivityIndicator,
    Button,
    HelperText,
    Searchbar,
    Text,
    useTheme,
} from 'react-native-paper'
import { useAuth } from '@/lib/auth'
import { startImpersonation, useImpersonation } from '@/lib/impersonation'
import { log } from '@/lib/logger'
import { PB_SERVER_ADDR } from '@/lib/pocketbase'
import { Card } from '@/ui/Card'
import { type Control, TextInput, useForm, z, zodResolver } from '@/ui/form'
import { Page } from '@/ui/Page'
import { UserCard } from '@/ui/people/UserCard'

const superuserAuthSchema = z.object({
    email: z.string().email('Please enter a valid email').min(1, 'Email is required'),
    password: z.string().min(1, 'Password is required'),
})

type SuperuserAuthFormData = z.infer<typeof superuserAuthSchema>

interface UserWithOrgs {
    id: string
    name: string
    email: string
    avatar?: string
    orgs: Array<{ id: string; name: string; slug: string; role: string }>
}

interface SuperuserAuthFormProps {
    control: Control<SuperuserAuthFormData>
    onSubmit: () => void
    isLoading: boolean
    isValid: boolean
    error: string | null
}

function SuperuserAuthForm({
    control,
    onSubmit,
    isLoading,
    isValid,
    error,
}: SuperuserAuthFormProps) {
    return (
        <Card>
            <Card.Content>
                <Text variant="titleMedium" style={styles.formTitle}>
                    Superuser Authentication
                </Text>
                <Text variant="bodyMedium" style={styles.formDescription}>
                    Enter your PocketBase superuser credentials to access impersonation features.
                </Text>

                <View style={styles.form}>
                    <TextInput
                        name="email"
                        control={control}
                        label="Superuser Email"
                        placeholder="admin@example.com"
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoComplete="email"
                    />

                    <TextInput
                        name="password"
                        control={control}
                        label="Superuser Password"
                        placeholder="Enter password"
                        secureTextEntry
                        autoComplete="current-password"
                    />

                    {error && (
                        <HelperText type="error" style={styles.error}>
                            {error}
                        </HelperText>
                    )}

                    <Button
                        mode="contained"
                        onPress={onSubmit}
                        disabled={isLoading || !isValid}
                        loading={isLoading}
                        style={styles.submitButton}
                    >
                        {isLoading ? 'Authenticating...' : 'Authenticate'}
                    </Button>
                </View>
            </Card.Content>
        </Card>
    )
}

function OrgsList({ orgs }: { orgs: UserWithOrgs['orgs'] }) {
    const theme = useTheme()
    if (orgs.length === 0) return null

    return (
        <View style={styles.orgsContainer}>
            {orgs.slice(0, 3).map(org => (
                <Text
                    key={org.id}
                    variant="bodySmall"
                    style={{ color: theme.colors.onSurfaceVariant }}
                >
                    {org.name} ({org.role})
                </Text>
            ))}
            {orgs.length > 3 && (
                <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                    +{orgs.length - 3} more
                </Text>
            )}
        </View>
    )
}

function ImpersonateUserCard({
    user,
    onImpersonate,
    isLoading,
}: {
    user: UserWithOrgs
    onImpersonate: () => void
    isLoading: boolean
}) {
    return (
        <UserCard
            user={{
                id: user.id,
                name: user.name,
                email: user.email,
                avatar: user.avatar,
            }}
            description={<OrgsList orgs={user.orgs} />}
            action={
                <Button
                    mode="contained-tonal"
                    onPress={onImpersonate}
                    loading={isLoading}
                    disabled={isLoading}
                    compact
                >
                    Impersonate
                </Button>
            }
        />
    )
}

export default function SuperuserPage() {
    const { orgSlug } = useLocalSearchParams<{ orgSlug: string }>()
    const router = useRouter()
    const { user } = useAuth()
    const { refreshFromStorage } = useImpersonation()

    const [superPb, setSuperPb] = useState<PocketBase | null>(null)
    const [authError, setAuthError] = useState<string | null>(null)
    const [isAuthenticating, setIsAuthenticating] = useState(false)

    const [users, setUsers] = useState<UserWithOrgs[]>([])
    const [isLoadingUsers, setIsLoadingUsers] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null)

    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<SuperuserAuthFormData>({
        resolver: zodResolver(superuserAuthSchema),
        defaultValues: { email: '', password: '' },
    })

    const isSuperUser = user?.metadata?.isSuperUser === true

    const loadUsers = useCallback(async (pb: PocketBase, query = '') => {
        setIsLoadingUsers(true)
        try {
            const filter = query ? `name ~ "${query}" || email ~ "${query}"` : ''

            const result = await pb.collection('users').getList(1, 50, {
                filter,
                sort: 'name',
                expand: 'user_org_via_user.org',
            })

            const usersWithOrgs: UserWithOrgs[] = result.items.map(item => {
                const userOrgs =
                    (item.expand?.user_org_via_user as Array<{
                        role: string
                        expand?: { org?: { id: string; name: string; slug: string } }
                    }>) || []

                return {
                    id: item.id,
                    name: item.name,
                    email: item.email,
                    avatar: item.avatar,
                    orgs: userOrgs.flatMap(uo => {
                        const org = uo.expand?.org
                        if (!org) return []
                        return [{ id: org.id, name: org.name, slug: org.slug, role: uo.role }]
                    }),
                }
            })

            setUsers(usersWithOrgs)
        } catch (err) {
            log.error('Failed to load users', err)
        } finally {
            setIsLoadingUsers(false)
        }
    }, [])

    const authenticateSuperuser = useCallback(
        async (data: SuperuserAuthFormData) => {
            setIsAuthenticating(true)
            setAuthError(null)

            try {
                const pb = new PocketBase(PB_SERVER_ADDR)
                await pb.collection('_superusers').authWithPassword(data.email, data.password)
                setSuperPb(pb)
                await loadUsers(pb)
            } catch (err) {
                setAuthError(
                    err instanceof Error ? err.message : 'Failed to authenticate as superuser'
                )
            } finally {
                setIsAuthenticating(false)
            }
        },
        [loadUsers]
    )

    const handleSearch = useCallback(
        (query: string) => {
            setSearchQuery(query)
            if (superPb) {
                loadUsers(superPb, query)
            }
        },
        [superPb, loadUsers]
    )

    const handleImpersonate = useCallback(
        async (userId: string) => {
            if (!superPb) return

            setImpersonatingUserId(userId)
            try {
                const impersonateClient = await superPb
                    .collection('users')
                    .impersonate(userId, 3600)

                const { pb } = await import('@/lib/pocketbase')
                const currentToken = pb.authStore.token
                const currentRecord = pb.authStore.record
                const primaryOrgSlug = user?.primaryOrgSlug || orgSlug

                if (!currentToken || !currentRecord) {
                    throw new Error('No current auth session')
                }

                const impersonateToken = impersonateClient.authStore.token
                const impersonateRecord = impersonateClient.authStore.record
                if (!impersonateToken || !impersonateRecord) {
                    throw new Error('Failed to get impersonation credentials')
                }

                const targetUser = users.find(u => u.id === userId)
                const targetOrgSlug = targetUser?.orgs[0]?.slug || orgSlug
                const redirectTo = `/${targetOrgSlug}`

                await startImpersonation(
                    impersonateToken,
                    impersonateRecord,
                    {
                        token: currentToken,
                        record: currentRecord,
                        primaryOrgSlug,
                    },
                    redirectTo
                )

                // On native, startImpersonation doesn't trigger a refresh,
                // so we need to update state and navigate manually
                if (Platform.OS !== 'web') {
                    await refreshFromStorage()
                    router.replace(redirectTo as '/')
                }
            } catch (err) {
                log.error('Failed to impersonate', err)
                setImpersonatingUserId(null)
            }
        },
        [superPb, user?.primaryOrgSlug, orgSlug, users, refreshFromStorage, router]
    )

    if (!isSuperUser) {
        return (
            <Page>
                <Card>
                    <Card.Content>
                        <Text variant="titleMedium">Access Denied</Text>
                        <Text variant="bodyMedium" style={styles.accessDeniedText}>
                            You do not have permission to access superuser tools.
                        </Text>
                        <Button
                            mode="outlined"
                            onPress={() => router.back()}
                            style={styles.backButton}
                        >
                            Go Back
                        </Button>
                    </Card.Content>
                </Card>
            </Page>
        )
    }

    if (!superPb) {
        return (
            <Page>
                <SuperuserAuthForm
                    control={control}
                    onSubmit={handleSubmit(authenticateSuperuser)}
                    isLoading={isAuthenticating}
                    isValid={isValid}
                    error={authError}
                />
            </Page>
        )
    }

    const listHeader = (
        <Card style={styles.searchCard}>
            <Card.Content>
                <Text variant="titleMedium" style={styles.cardTitle}>
                    Impersonate User
                </Text>
                <Text variant="bodyMedium" style={styles.cardDescription}>
                    Select a user to view the app as they would see it.
                </Text>

                <Searchbar
                    placeholder="Search by name or email"
                    value={searchQuery}
                    onChangeText={handleSearch}
                    style={styles.searchbar}
                />
            </Card.Content>
        </Card>
    )

    return (
        <Page scrollable={false} noPad>
            <FlatList
                data={users}
                keyExtractor={item => item.id}
                ListHeaderComponent={listHeader}
                ItemSeparatorComponent={() => <View style={styles.separator} />}
                renderItem={({ item }) => (
                    <ImpersonateUserCard
                        user={item}
                        onImpersonate={() => handleImpersonate(item.id)}
                        isLoading={impersonatingUserId === item.id}
                    />
                )}
                ListEmptyComponent={
                    isLoadingUsers ? (
                        <ActivityIndicator style={styles.loader} />
                    ) : (
                        <Card>
                            <Card.Content>
                                <Text variant="bodyMedium" style={styles.emptyText}>
                                    No users found
                                </Text>
                            </Card.Content>
                        </Card>
                    )
                }
                contentContainerStyle={styles.listContent}
            />
        </Page>
    )
}

const styles = StyleSheet.create({
    formTitle: {
        fontWeight: '600',
        marginBottom: 8,
    },
    formDescription: {
        opacity: 0.7,
        marginBottom: 16,
    },
    form: {
        gap: 16,
    },
    error: {
        marginTop: -8,
    },
    submitButton: {
        marginTop: 8,
    },
    accessDeniedText: {
        opacity: 0.7,
        marginTop: 8,
        marginBottom: 16,
    },
    backButton: {
        alignSelf: 'flex-start',
    },
    searchCard: {
        marginBottom: 8,
    },
    cardTitle: {
        fontWeight: '600',
        marginBottom: 4,
    },
    cardDescription: {
        opacity: 0.7,
        marginBottom: 16,
    },
    searchbar: {
        elevation: 0,
    },
    loader: {
        marginTop: 32,
    },
    separator: {
        height: 8,
    },
    listContent: {
        padding: 16,
        paddingBottom: 24,
    },
    emptyText: {
        textAlign: 'center',
        opacity: 0.7,
    },
    orgsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 4,
        alignItems: 'center',
    },
})
